import FlowsByModel from 'types/FlowsByModel';
import FlowsByOrigin from 'types/FlowsByOrigin';
import { PartteamModel, ShareboxModel, KioskType } from 'types/KioskTypes';
import { SelfServiceStepConfig } from 'modules/selfServices/types/SelfServiceStepConfig';
import {
  FlowType,
  QUICK_CHECK_IN,
  SelfService,
  SelfServiceOrigin,
  SelfServiceType
} from 'modules/selfServices/types/SelfService';

import { getFollowingStep } from 'utils/stepUtils';

import { isVisibleStep } from './utils';
import CHECK_IN_STEPS from './check-in';
import CHECK_OUT_STEPS from './check-out';
import BREAKDOWN_STEPS from './breakdown';
import type { SelfServiceStep } from '../types';

type OriginFlowsBySelfServiceType = Record<SelfServiceType, FlowsByOrigin>;

const STEPS: OriginFlowsBySelfServiceType = {
  [SelfServiceType.CHECK_IN]: CHECK_IN_STEPS,
  [SelfServiceType.CHECK_OUT]: CHECK_OUT_STEPS,
  [SelfServiceType.BREAKDOWN]: BREAKDOWN_STEPS,
};

const getAllSteps = (selfService: SelfService, config: SelfServiceStepConfig) => {
  const selfServiceTypeFlows = STEPS[selfService?.type] ?? {};

  let selfServiceSteps: SelfServiceStep[];
  if (selfService?.origin === SelfServiceOrigin.KIOSK) {
    const model = config.kioskModel;

    if (config.kioskType === KioskType.SHAREBOX) {
      const flowsByModel = selfServiceTypeFlows.KIOSK[config.kioskType] as FlowsByModel<ShareboxModel>;
      selfServiceSteps = Object.values(flowsByModel[model as ShareboxModel]);
    } else {
      const flowsByModel = selfServiceTypeFlows.KIOSK[config.kioskType] as FlowsByModel<PartteamModel>;
      selfServiceSteps = Object.values(flowsByModel[model as PartteamModel]);
    }
  } else {
    selfServiceSteps = Object.values(selfServiceTypeFlows[selfService?.origin] ?? {});
  }

  return selfServiceSteps;
};

const getFinalizeStep = (steps: SelfServiceStep[], selfService: SelfService, config: SelfServiceStepConfig) => steps.find((step) => {
  const isFinalize = step.data?.isFinalizeStep;
  return typeof isFinalize === 'function' ? isFinalize(selfService, config) : Boolean(isFinalize);
});

/** All the steps visible for this self-service/configuration */
export const getVisibleSteps = (steps: SelfServiceStep[], selfService: SelfService, config: SelfServiceStepConfig) => (
  steps.filter((step) => {
    const flowType = config.flowType === FlowType.QUICK ? QUICK_CHECK_IN : selfService.type;

    // Any step that does not exist in the refdata should be excluded from workflow tests.
    const shouldCheckStep = config.workflows[flowType]?.[step.key] !== undefined;
    const isIncludedInWorkflow = config.workflows[flowType]?.[step.key]?.mandatory
      || selfService.dealer.workflows?.[flowType]?.includes(step.key);

    // Only include steps that exist in refdata AND are either mandatory or explicitly enabled in the selfService workflow
    return (!shouldCheckStep || isIncludedInWorkflow) && isVisibleStep(step, selfService, config);
  })
);

export const getSelfServiceSteps = (selfService: SelfService, config: SelfServiceStepConfig) => {
  const selfServiceSteps = getAllSteps(selfService, config); // All possible steps for this flow
  let finalizeStep = getFinalizeStep(selfServiceSteps, selfService, config); // The last possible finalize step
  const steps = getVisibleSteps(selfServiceSteps, selfService, config);

  // If the finalizeStep is not visible we should get the first previous visible one
  let stepKey: string;
  if (steps.every((step) => step.key !== finalizeStep.key)) {
    stepKey = getFollowingStep<SelfServiceStep>(finalizeStep, steps, selfServiceSteps, 'previous');
    if (stepKey) {
      finalizeStep = steps.find((step) => step.key === stepKey);
    }
  }

  return steps.map((step) => ({
    ...step,
    next: getFollowingStep<SelfServiceStep>(step, steps, selfServiceSteps),
    previous: getFollowingStep<SelfServiceStep>(step, steps, selfServiceSteps, 'previous'),
    data: {
      ...step.data,
      isFinalizeStep: step.key === finalizeStep?.key,
    },
  }));
};

// Looked up in the unfiltered list because the isLastHomeStep step itself may be hidden.
export const getNextStepAfterHome = (selfService: SelfService, config: SelfServiceStepConfig) => {
  const selfServiceSteps = getAllSteps(selfService, config);
  const lastHomeStep = selfServiceSteps.find((step) => step.data?.isLastHomeStep);

  if (!lastHomeStep) {
    return undefined;
  }

  const steps = getVisibleSteps(selfServiceSteps, selfService, config);
  return getFollowingStep<SelfServiceStep>(lastHomeStep, steps, selfServiceSteps);
};

export default STEPS;
